import { MasterTranslation, TextStyles } from '@/constants';
import { useAuth, useGlobalState, useResponsive } from '@/hooks';
import { ResponsiveCollection } from '@/interfaces';
import { useEmailSyncContext } from '@/modules/pmt/context';
import LogoutSvgIcon from '@/public/images/icons/logout.svg';
import DataPrivacySvgIcon from '@/public/images/icons/privacy-tip.svg';
import { type FC, useMemo, useState } from 'react';
import LoginBanner from '../../LoginBanner';
import { AccountTitle, AccountWrapper, LoginBannerWrapper, NotificationsIcon, SyncIcon } from '../Account.styles';
import AccountSection from '../AccountSection';
import { EmailAccounts } from '../EmailAccounts';
import { LogoutConfirmDialog } from '../LogoutConfirmDialog';
import { Notifications } from '../Notifications';

interface AccountViewProps {
  onNavigateToDataPrivacy: () => void;
}

const AccountView: FC<AccountViewProps> = ({ onNavigateToDataPrivacy }) => {
  const { logout } = useAuth();
  const { t, isLoggedIn } = useGlobalState();
  const { hasEmailAccounts, isFetchingEmailAccounts } = useEmailSyncContext();
  const { isDesktopPMT } = useResponsive([ResponsiveCollection.DesktopPMT]);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  const handleLogout = async () => {
    await logout();
    setShowLogoutDialog(false);
  };

  const onLogoutClick = () => {
    setShowLogoutDialog(true);
  };

  const emailAccountsSectionText = useMemo(() => {
    if (!isLoggedIn) {
      return {
        title: t(MasterTranslation.AccSyncedEmailsTitleEmpty),
        subText: t(MasterTranslation.AccSyncedEmailsSubtitleLoading), // The subtext is the same as 'isFetchingEmailAccounts'
      };
    }

    if (isFetchingEmailAccounts) {
      return {
        title: t(MasterTranslation.AccSyncedEmailsTitleLoading),
        subText: t(MasterTranslation.AccSyncedEmailsSubtitleLoading),
      };
    }

    if (hasEmailAccounts) {
      return {
        title: t(MasterTranslation.AccSyncedEmailsTitleSucceed),
        subText: t(MasterTranslation.AccSyncedEmailsSubtitleSucceed),
      };
    }

    return {
      title: t(MasterTranslation.AccSyncedEmailsTitleEmpty),
      subText: t(MasterTranslation.AccSyncedEmailsSubtitleLoading), // The subtext is the same as 'isFetchingEmailAccounts'
    };
  }, [hasEmailAccounts, isFetchingEmailAccounts, isLoggedIn, t]);

  return (
    <AccountWrapper>
      <AccountTitle variant={isDesktopPMT ? TextStyles['Heading 1'] : TextStyles['Heading 2']}>
        {t(MasterTranslation.AccSettingsSection)}
      </AccountTitle>

      {/* Login banner - Only show at this position on desktop */}
      <LoginBannerWrapper showOn='desktop'>
        <LoginBanner isDesktopPMT={isDesktopPMT} />
      </LoginBannerWrapper>

      {/* Synced Emails */}
      <AccountSection
        title={emailAccountsSectionText.title}
        subText={emailAccountsSectionText.subText}
        icon={<SyncIcon />}
      >
        <EmailAccounts />
      </AccountSection>

      {/* Notifications */}
      <AccountSection
        title={t(MasterTranslation.AccNotificationsTitle)}
        subText={t(MasterTranslation.AccNotificationsSubtitle)}
        icon={<NotificationsIcon />}
      >
        <Notifications />
      </AccountSection>

      {/* Data & Privacy */}
      <AccountSection
        title={t(MasterTranslation.AccDataAndPrivacy)}
        icon={<DataPrivacySvgIcon />}
        hasArrow
        onClick={onNavigateToDataPrivacy}
        showCTAButton={false}
      />

      {/* Log Out */}
      {isLoggedIn && (
        <AccountSection
          onClick={onLogoutClick}
          title={t(MasterTranslation.AccLogoutCtaCaps)}
          icon={<LogoutSvgIcon />}
        />
      )}

      {/* Logout Confirmation Dialog */}
      <LogoutConfirmDialog showDialog={showLogoutDialog} setShowDialog={setShowLogoutDialog} onLogout={handleLogout} />
    </AccountWrapper>
  );
};

export default AccountView;
